import React, { useState, useRef, useEffect, useMemo } from "react";
import { collegeInNewsApi } from "../../config/api";
import { apiClient, apiFormDataClient } from "../../utils/apiClient";
import { compressImage } from "../../utils/imageCompressor";
import "./CollegeInNews.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = ["All", "Newspaper", "Television", "Online Media", "Magazine", "Achievement", "Award", "Event", "Other"];

const BLANK_FORM = {
  _id: null,
  News_Title: "",
  Source_Publication: "",
  Category: "Newspaper",
  Published_Date: new Date().toISOString().split('T')[0],
  Description: "",
  Image_URL: "",
  External_Link: "",
  Mark_as_Featured: false,
  Visible_on_Website: true,
};

const CATEGORY_COLORS = {
  Newspaper: { bg: "#eff6ff", color: "#1e40af", border: "#bfdbfe" },
  Television: { bg: "#fdf4ff", color: "#7c3aed", border: "#e9d5ff" },
  "Online Media": { bg: "#f0fdf4", color: "#166534", border: "#bbf7d0" },
  Magazine: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  Achievement: { bg: "#fefce8", color: "#854d0e", border: "#fde68a" },
  Award: { bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0" },
  Event: { bg: "#fff5f5", color: "#991b1b", border: "#fecaca" },
  Other: { bg: "#f8fafc", color: "#475569", border: "#e2e8f0" },
};

const CollegeInNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(""); // "add", "edit", "view"
  const [form, setForm] = useState(BLANK_FORM);
  const [confirmId, setConfirmId] = useState(null);
  const [dropdown, setDropdown] = useState({ id: null, top: 0, left: 0 });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => { fetchNews(); }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await apiClient(collegeInNewsApi);
      const result = await response.json();
      if (response.ok) { setNews(result.data || []); }
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdown({ id: null, top: 0, left: 0 }); };
    const onScroll = () => setDropdown({ id: null, top: 0, left: 0 });
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return news.filter((n) => {
      const matchCat = activeCategory === "All" || n.Category === activeCategory;
      const matchSearch = !search || 
        (n.News_Title || "").toLowerCase().includes(search.toLowerCase()) || 
        (n.Source_Publication || "").toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [news, activeCategory, search]);

  const stats = useMemo(() => ({
    total: news.length,
    featured: news.filter(n => n.Mark_as_Featured).length,
    hidden: news.filter(n => !n.Visible_on_Website).length,
    latest: news.length > 0 ? new Date(Math.max(...news.map(n => new Date(n.Published_Date)))).toLocaleDateString() : "—"
  }), [news]);

  const categoryCounts = useMemo(() => {
    const counts = { All: news.length };
    CATEGORIES.filter(c => c !== "All").forEach(cat => {
      counts[cat] = news.filter(n => n.Category === cat).length;
    });
    return counts;
  }, [news]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const openDropdown = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdown({ id, top: rect.bottom + 8, left: rect.right - 200 });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const compressed = await compressImage(file);
      setSelectedFile(compressed);
      setPreviewUrl(URL.createObjectURL(compressed));
    }
  };

  const removePhoto = () => {
    setForm(p => ({ ...p, Image_URL: "" }));
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const saveNews = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modal === "add" ? "POST" : "PUT";
      const url = modal === "add" ? collegeInNewsApi : `${collegeInNewsApi}/${form._id}`;
      
      const formDataToSend = new FormData();
      Object.keys(form).forEach(key => {
        if (key !== 'Image_URL' && form[key] !== null) {
          formDataToSend.append(key, form[key]);
        }
      });

      if (selectedFile) {
        const compressed = await compressImage(selectedFile);
        formDataToSend.append('Image_URL', compressed);
      } else if (form.Image_URL) {
        formDataToSend.append('Image_URL', form.Image_URL);
      }

      const response = await apiFormDataClient(url, { method, body: formDataToSend });
      if (response.ok) {
        fetchNews();
        setModal("");
      } else {
        const errJson = await response.json().catch(() => ({}));
        alert(`Failed to save news article: ${errJson.message || response.statusText || 'Server error'}`);
      }
    } catch (error) {
      console.error("Error saving news:", error);
      alert(`Error saving news article: ${error.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteNews = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`${collegeInNewsApi}/${confirmId}`, { method: "DELETE" });
      if (response.ok) { fetchNews(); setConfirmId(null); }
    } catch (error) {
      console.error("Error deleting news:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFeatured = async (id) => {
    const item = news.find(n => n._id === id);
    if (!item) return;
    try {
      await apiClient(`${collegeInNewsApi}/${id}`, {
        method: "PUT",
        body: JSON.stringify({ ...item, Mark_as_Featured: !item.Mark_as_Featured }),
      });
      fetchNews();
    } catch (error) { console.error(error); }
  };

  const toggleVisibility = async (id) => {
    const item = news.find(n => n._id === id);
    if (!item) return;
    try {
      await apiClient(`${collegeInNewsApi}/${id}`, {
        method: "PUT",
        body: JSON.stringify({ ...item, Visible_on_Website: !item.Visible_on_Website }),
      });
      fetchNews();
    } catch (error) { console.error(error); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="cin-page">
      {/* ── Header ── */}
      <div className="cin-header">
        <div className="cin-header-left">
          <div className="cin-header-icon">🗞️</div>
          <div>
            <div className="cin-breadcrumb">
              <span>Media & Contact</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              <span className="cin-bc-active">College in News</span>
            </div>
            <h1>College in News</h1>
            <p>Managing the college's public image and media presence across various platforms.</p>
          </div>
        </div>
        <div className="cin-header-actions">
           <button className="cin-btn-primary" onClick={() => { setForm(BLANK_FORM); setSelectedFile(null); setPreviewUrl(null); setModal("add"); }}>
             <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" style={{marginRight:6}}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
             Add News Coverage
           </button>
        </div>
      </div>

      {loading && <div className="loading-overlay">Syncing media records...</div>}

      {/* ── Stats Row ── */}
      <div className="cin-stats-row">
        <div className="cin-stat-card"><span className="cin-stat-icon">📑</span><div><strong>{stats.total}</strong><p>Total Coverage</p></div></div>
        <div className="cin-stat-card" style={{borderTopColor: '#f59e0b'}}><span className="cin-stat-icon">⭐</span><div><strong>{stats.featured}</strong><p>Featured News</p></div></div>
        <div className="cin-stat-card" style={{borderTopColor: '#0ea5e9'}}><span className="cin-stat-icon">📅</span><div><strong>{stats.latest}</strong><p>Latest Mention</p></div></div>
        <div className="cin-stat-card" style={{borderTopColor: '#94a3b8'}}><span className="cin-stat-icon">🔒</span><div><strong>{stats.hidden}</strong><p>Hidden Items</p></div></div>
      </div>

      {/* ── Filters ── */}
      <div className="cin-filters">
        <div className="cin-search">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input type="text" placeholder="Search by title or source..." value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <button className="cin-clr-search" onClick={() => setSearch("")}>×</button>}
        </div>
        <div className="cin-cat-tabs">
          {CATEGORIES.map((cat) => (
            <button key={cat} className={`cin-cat-tab ${activeCategory === cat ? "active" : ""}`} onClick={() => setActiveCategory(cat)}>
              {cat} <span className="cin-cat-count">{categoryCounts[cat] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── News Grid ── */}
      <div className="cin-news-grid">
        {filtered.map((item) => {
          const cc = CATEGORY_COLORS[item.Category] || CATEGORY_COLORS["Other"];
          return (
            <div className={`cin-news-card ${!item.Visible_on_Website ? "hidden-card" : ""}`} key={item._id}>
              <div className="cin-card-top">
                <span className="cin-cat-badge" style={{ background: cc.bg, color: cc.color, border: `1px solid ${cc.border}` }}>{item.Category}</span>
                <div className="cin-card-top-right">
                  {item.Mark_as_Featured && <span className="cin-featured-badge">⭐ Featured</span>}
                  {!item.Visible_on_Website && <span className="cin-hidden-badge">🔒 Hidden</span>}
                  <button className="cin-dots" onClick={(e) => openDropdown(e, item._id)}>⋮</button>
                </div>
              </div>
              
              <div className="cin-card-img">
                <img src={item.Image_URL || "https://placehold.co/600x400?text=News+Coverage"} alt="News" />
              </div>

              <div className="cin-card-body">
                <h3 onClick={() => { setForm(item); setModal("view"); }}>{item.News_Title}</h3>
                <div className="cin-card-meta">
                  <span>🏛️ {item.Source_Publication}</span>
                  <span>📅 {formatDate(item.Published_Date)}</span>
                </div>
                <p className="cin-card-desc">{item.Description}</p>
                {item.External_Link && (
                  <a href={item.External_Link} target="_blank" rel="noreferrer" className="cin-ext-link">
                    Read Full Article <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                  </a>
                )}
              </div>
              <div className="cin-card-footer">
                <button className="cin-footer-btn edit" onClick={() => { setForm(item); setModal("edit"); }}>Edit</button>
                <button className="cin-footer-btn view" onClick={() => { setForm(item); setModal("view"); }}>View</button>
                <button className="cin-footer-btn del" onClick={() => setConfirmId(item._id)}>Delete</button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="cin-empty" style={{ gridColumn: '1 / -1' }}>
            <div>🗞️</div>
            <h3>No news coverage found</h3>
            <p>Try adjusting your search or filters to see more media mentions.</p>
          </div>
        )}
      </div>

      {/* ── Dropdown ── */}
      {dropdown.id && (
        <div ref={dropdownRef} className="cin-dropdown" style={{ position: "fixed", top: dropdown.top, left: dropdown.left, zIndex: 99999 }}>
          <button onClick={() => { toggleFeatured(dropdown.id); setDropdown({id:null}); }}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
            Toggle Featured
          </button>
          <button onClick={() => { toggleVisibility(dropdown.id); setDropdown({id:null}); }}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
            Toggle Visibility
          </button>
        </div>
      )}

      {/* ── Add/Edit Modal ── */}
      {(modal === "add" || modal === "edit") && (
        <div className="cin-overlay" onClick={() => setModal("")}>
          <div className="cin-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cin-modal-head">
              <div><h2>{modal === "add" ? "Add News" : "Edit Coverage"}</h2><p>Provide details about the media mention or news article.</p></div>
              <button className="cin-modal-close" onClick={() => setModal("")}>×</button>
            </div>
            <form className="cin-form" onSubmit={saveNews}>
              <div className="cin-field full"><label>News Title <span className="req">*</span></label><input type="text" required value={form.News_Title || ""} onChange={(e) => setForm({ ...form, News_Title: e.target.value })} placeholder="e.g. Rajat College excels in University Rankings" /></div>
              <div className="cin-field-row">
                <div className="cin-field"><label>Source Publication <span className="req">*</span></label><input type="text" required value={form.Source_Publication || ""} onChange={(e) => setForm({ ...form, Source_Publication: e.target.value })} placeholder="e.g. The Times of India" /></div>
                <div className="cin-field"><label>Category <span className="req">*</span></label><select value={form.Category} onChange={(e) => setForm({ ...form, Category: e.target.value })}>{CATEGORIES.filter(c => c !== "All").map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              </div>
              <div className="cin-field-row">
                <div className="cin-field"><label>Published Date <span className="req">*</span></label><input type="date" required value={form.Published_Date ? form.Published_Date.split('T')[0] : ""} onChange={(e) => setForm({ ...form, Published_Date: e.target.value })} /></div>
                <div className="cin-field"><label>External Link</label><input type="url" value={form.External_Link || ""} onChange={(e) => setForm({ ...form, External_Link: e.target.value })} placeholder="https://..." /></div>
              </div>
              <div className="cin-field full"><label>Brief Description <span className="req">*</span></label><textarea required value={form.Description || ""} onChange={(e) => setForm({ ...form, Description: e.target.value })} rows="3" placeholder="Summarize the news content..." /></div>
              
              <div className="cin-field full">
                <label>Article Thumbnail / Clipping</label>
                <div className="cin-photo-upload">
                  {(previewUrl || form.Image_URL) ? (
                    <div className="cin-photo-preview">
                      <img src={previewUrl || form.Image_URL} alt="Preview" />
                      <div className="cin-photo-actions">
                        <label className="cin-change-btn">Change Image<input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} /></label>
                        <button type="button" className="cin-remove-btn" onClick={removePhoto}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <label className="cin-upload-placeholder">
                      <div className="cin-upload-icon">📸</div>
                      <span>Select News Clipping / Thumbnail</span>
                      <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                    </label>
                  )}
                </div>
              </div>

              <div className="cin-toggle-row">
                <label className="cin-toggle-item">
                  <input type="checkbox" checked={form.Mark_as_Featured || false} onChange={(e) => setForm({ ...form, Mark_as_Featured: e.target.checked })} />
                  <div className="cin-chk-box"></div>
                  <div><strong>Feature on Home</strong><p>Show this mention in the 'Highlights' section of the website.</p></div>
                </label>
                <label className="cin-toggle-item">
                  <input type="checkbox" checked={form.Visible_on_Website || false} onChange={(e) => setForm({ ...form, Visible_on_Website: e.target.checked })} />
                  <div className="cin-chk-box"></div>
                  <div><strong>Publish Coverage</strong><p>Make this news clipping visible in the media gallery.</p></div>
                </label>
              </div>

              <div className="cin-form-footer"><button type="button" className="cin-btn-light" onClick={() => setModal("")}>Cancel</button><button type="submit" className="cin-btn-primary">Save Media Record</button></div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {modal === "view" && (
        <div className="cin-overlay" onClick={() => setModal("")}>
          <div className="cin-form-modal small" onClick={e => e.stopPropagation()}>
            <div className="cin-modal-head">
              <div><h2>Coverage Details</h2><p>Full view of the media mention.</p></div>
              <button className="cin-modal-close" onClick={() => setModal("")}>×</button>
            </div>
            <div className="cin-view-body">
              <div className="cin-view-badges">
                <span className="cin-cat-badge" style={{ background: CATEGORY_COLORS[form.Category]?.bg, color: CATEGORY_COLORS[form.Category]?.color, border: `1px solid ${CATEGORY_COLORS[form.Category]?.border}` }}>{form.Category}</span>
                {form.Mark_as_Featured && <span className="cin-featured-badge">⭐ Featured Coverage</span>}
              </div>
              <img src={form.Image_URL || "https://placehold.co/600x400?text=News+Coverage"} alt="News" className="cin-view-img" />
              <h2 className="cin-view-title">{form.News_Title}</h2>
              <div className="cin-view-meta">
                <span>🏛️ <strong>Source:</strong> {form.Source_Publication}</span>
                <span>📅 <strong>Date:</strong> {formatDate(form.Published_Date)}</span>
              </div>
              <p className="cin-view-desc">{form.Description}</p>
              {form.External_Link && (
                 <a href={form.External_Link} target="_blank" rel="noreferrer" className="cin-ext-link">
                    Open Source Article Online →
                 </a>
              )}
              <div className="cin-view-actions">
                <button className="cin-footer-btn edit" onClick={() => setModal("edit")}>Edit Record</button>
                <button className="cin-footer-btn del" onClick={() => { setConfirmId(form._id); setModal(""); }}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Delete ── */}
      {confirmId && (
        <div className="cin-overlay" onClick={() => setConfirmId(null)}>
          <div className="cin-confirm" onClick={e => e.stopPropagation()}>
            <div className="cin-confirm-icon">🗑️</div>
            <h3>Delete this record?</h3>
            <p>This action will permanently remove this news coverage from the gallery. This cannot be undone.</p>
            <div className="cin-confirm-actions">
              <button className="cin-btn-light" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="cin-btn-danger" onClick={deleteNews}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollegeInNews;